import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DollarSign,
  Clock,
  TrendingUp,
  Download,
  Calendar as CalendarIcon,
  AlertCircle,
  Lock,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  KeyRound,
  Settings,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

// Manager PIN for payroll access (in production, this should be stored securely per user)
const MANAGER_PAYROLL_PIN = '1234';

// Default payroll cost multiplier (includes taxes, benefits, etc.)
const DEFAULT_COST_MULTIPLIER = 1.25;

const Payroll = () => {
  const { user, selectedLocation, locations, isAccountingAdmin } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [showManagerSalaries, setShowManagerSalaries] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payrollSummary, setPayrollSummary] = useState({
    totalCost: 0,
    totalHours: 0,
    employeeBreakdown: [],
    managerPayroll: [],
    totalManagerCost: 0,
    startDate: '',
    endDate: '',
  });
  
  // Date range selection
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Cost multiplier
  const [costMultiplier, setCostMultiplier] = useState(DEFAULT_COST_MULTIPLIER);
  const [showMultiplierSettings, setShowMultiplierSettings] = useState(false);
  const [tempMultiplier, setTempMultiplier] = useState(DEFAULT_COST_MULTIPLIER.toString());
  
  // PIN verification for managers
  const [pinVerified, setPinVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  // Use context helper for accounting admin check
  const canViewSalaries = isAccountingAdmin ? isAccountingAdmin() : false;
  const canEditMultiplier = canViewSalaries;
  const hasManagerRole = user?.role === 'manager' || (user?.roles || []).includes('manager');
  const hasAdminRole = user?.role === 'admin' || user?.role === 'accounting_admin' || 
                       (user?.roles || []).includes('admin') || (user?.roles || []).includes('accounting_admin');
  const requiresPin = hasManagerRole && !hasAdminRole; // Managers need PIN unless they're also admin
  const isAssistantManager = user?.role === 'assistant_manager' && !hasAdminRole && !hasManagerRole;

  // Show PIN modal for managers on first access
  useEffect(() => {
    if (requiresPin && !pinVerified) {
      setShowPinModal(true);
    }
  }, [requiresPin, pinVerified]);

  const handlePinSubmit = async () => {
    // Check against user's stored PIN (default 1234)
    const userPin = user?.payroll_pin || '1234';
    if (pinInput === userPin) {
      setPinVerified(true);
      setShowPinModal(false);
      setPinError('');
      setPinInput('');
    } else {
      setPinError('Invalid PIN. Contact your Admin to reset your PIN.');
    }
  };

  useEffect(() => {
    if (!isAssistantManager && (!requiresPin || pinVerified)) {
      loadPayrollData();
    }
  }, [selectedLocation, period, showManagerSalaries, pinVerified, isAssistantManager, customStartDate, customEndDate]);

  const loadPayrollData = async () => {
    if (requiresPin && !pinVerified) return;
    if (isAssistantManager) return;
    
    setLoading(true);
    try {
      const params = {
        locationId: selectedLocation,
        period: useCustomRange ? 'custom' : period,
        includeManagerSalaries: canViewSalaries && showManagerSalaries,
      };
      
      // Add custom date range params if using custom period
      if (useCustomRange && customStartDate && customEndDate) {
        params.startDate = format(customStartDate, 'yyyy-MM-dd');
        params.endDate = format(customEndDate, 'yyyy-MM-dd');
      }
      
      const data = await payrollApi.getSummary(params);
      setPayrollSummary({
        totalCost: data.total_cost || 0,
        totalHours: data.total_hours || 0,
        employeeBreakdown: data.employee_breakdown || [],
        managerPayroll: data.manager_payroll || [],
        totalManagerCost: data.total_manager_cost || 0,
        startDate: data.start_date || '',
        endDate: data.end_date || '',
      });
    } catch (error) {
      console.error('Failed to load payroll:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayroll = loadPayrollData; // Alias for backward compatibility

  // Assistant managers cannot access payroll at all
  if (isAssistantManager) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">
            Assistant Managers do not have access to payroll information.
          </p>
        </div>
      </div>
    );
  }

  // Show PIN verification for managers
  if (requiresPin && !pinVerified) {
    return (
      <div className="p-8">
        <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-purple-600" />
                Payroll Access Verification
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Please enter your manager PIN to access payroll information.
              </p>
              <div className="space-y-2">
                <Label>PIN Code</Label>
                <Input
                  type="password"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                  placeholder="Enter 4-digit PIN"
                  maxLength={4}
                  className="text-center text-2xl tracking-widest"
                  data-testid="payroll-pin-input"
                />
              </div>
              {pinError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {pinError}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Default PIN: 1234 (Contact admin to change)
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={handlePinSubmit}
                className="bg-purple-600 hover:bg-purple-700 w-full"
                data-testid="verify-pin-btn"
              >
                Verify & Access Payroll
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <div className="text-center py-12">
          <Lock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">PIN Required</h2>
          <p className="text-gray-500 mt-2">
            Please enter your PIN to access payroll information.
          </p>
          <Button 
            onClick={() => setShowPinModal(true)} 
            className="mt-4 bg-purple-600 hover:bg-purple-700"
          >
            Enter PIN
          </Button>
        </div>
      </div>
    );
  }

  const periodLabels = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    annual: 'This Year',
    custom: 'Custom Range',
  };

  // Calculate Total Payroll Cost with multiplier
  const actualPayroll = payrollSummary.totalCost;
  const totalPayrollCost = actualPayroll * costMultiplier;

  const stats = [
    {
      title: 'Actual Payroll',
      value: `$${actualPayroll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'bg-purple-500',
      description: 'Hours worked × Rate',
    },
    {
      title: 'Total Payroll Cost',
      value: `$${totalPayrollCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: 'bg-green-500',
      description: `Actual × ${costMultiplier.toFixed(2)} (taxes, fees)`,
    },
    {
      title: 'Total Hours',
      value: Math.round(payrollSummary.totalHours),
      icon: Clock,
      color: 'bg-blue-500',
      description: `${payrollSummary.employeeBreakdown.length} employees`,
    },
    {
      title: 'Overtime Hours',
      value: payrollSummary.employeeBreakdown.reduce((sum, r) => sum + (r.overtime_hours || 0), 0).toFixed(1),
      icon: AlertCircle,
      color: 'bg-amber-500',
      description: '1.5x rate applied',
    },
  ];

  const handleSaveMultiplier = () => {
    const val = parseFloat(tempMultiplier);
    if (!isNaN(val) && val > 0) {
      setCostMultiplier(val);
      setShowMultiplierSettings(false);
    }
  };

  const handleExportCSV = () => {
    const periodStr = useCustomRange ? 'custom' : period;
    const dateRange = payrollSummary.startDate && payrollSummary.endDate
      ? `${payrollSummary.startDate} to ${payrollSummary.endDate}`
      : periodStr;

    const lines = [
      `Payroll Report`,
      `Period: ${dateRange}`,
      `Location: ${selectedLocation === 'all' ? 'All Locations' : (locations || []).find(l => l.id === selectedLocation)?.name || selectedLocation}`,
      `Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`,
      ``,
    ];

    const headers = ['Employee', 'Employee ID', 'Position', 'Hourly Rate', 'Regular Hours', 'OT Hours', 'Total Hours', 'Regular Pay', 'OT Pay', 'Total Pay'];
    lines.push(headers.join(','));

    let totalReg = 0, totalOT = 0, totalHrs = 0, totalPay = 0;
    payrollSummary.employeeBreakdown
      .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0))
      .forEach((r) => {
        const rate = r.employee?.hourly_rate || 0;
        const regH = r.regular_hours || 0;
        const otH = r.overtime_hours || 0;
        const totH = r.total_hours || 0;
        const regPay = regH * rate;
        const otPay = otH * rate * 1.5;
        totalReg += regH; totalOT += otH; totalHrs += totH; totalPay += (r.total_pay || 0);
        lines.push([
          `"${r.employee?.name || ''}"`,
          r.employee?.employee_number || '',
          `"${r.employee?.position || ''}"`,
          `$${rate.toFixed(2)}`,
          regH.toFixed(2),
          otH.toFixed(2),
          totH.toFixed(2),
          `$${regPay.toFixed(2)}`,
          `$${otPay.toFixed(2)}`,
          `$${(r.total_pay || 0).toFixed(2)}`,
        ].join(','));
      });

    lines.push('');
    lines.push(['TOTALS', '', '', '', totalReg.toFixed(2), totalOT.toFixed(2), totalHrs.toFixed(2), '', '', `$${totalPay.toFixed(2)}`].join(','));
    lines.push(`Total Payroll Cost (×${costMultiplier.toFixed(2)}):,$${(totalPay * costMultiplier).toFixed(2)}`);

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${payrollSummary.startDate || format(new Date(), 'yyyy-MM-dd')}-to-${payrollSummary.endDate || format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const locName = selectedLocation === 'all' ? 'All Locations' : (locations || []).find(l => l.id === selectedLocation)?.name || selectedLocation;
    const dateRange = payrollSummary.startDate && payrollSummary.endDate
      ? `${format(parseISO(payrollSummary.startDate), 'MMM d, yyyy')} – ${format(parseISO(payrollSummary.endDate), 'MMM d, yyyy')}`
      : period;

    // Header
    doc.setFillColor(30, 30, 46);
    doc.rect(0, 0, pageWidth, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Payroll Report', 14, 12);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${locName}  |  ${dateRange}`, 14, 20);
    doc.text(`Generated ${format(new Date(), 'MMM d, yyyy h:mm a')}`, pageWidth - 14, 20, { align: 'right' });

    // Summary boxes
    const totalOT = payrollSummary.employeeBreakdown.reduce((s, r) => s + (r.overtime_hours || 0), 0);
    const summaryY = 34;
    const boxW = (pageWidth - 70) / 4;
    const summaryItems = [
      { label: 'Actual Payroll', value: `$${payrollSummary.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
      { label: `Total Cost (×${costMultiplier.toFixed(2)})`, value: `$${(payrollSummary.totalCost * costMultiplier).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
      { label: 'Total Hours', value: payrollSummary.totalHours.toFixed(1) },
      { label: 'Overtime Hours', value: totalOT.toFixed(1) },
    ];
    summaryItems.forEach((item, i) => {
      const x = 14 + i * (boxW + 14);
      doc.setFillColor(245, 245, 250);
      doc.roundedRect(x, summaryY, boxW, 16, 2, 2, 'F');
      doc.setTextColor(100, 100, 120);
      doc.setFontSize(8);
      doc.text(item.label, x + 4, summaryY + 6);
      doc.setTextColor(30, 30, 46);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(item.value, x + 4, summaryY + 13);
      doc.setFont('helvetica', 'normal');
    });

    // Employee table
    const tableData = payrollSummary.employeeBreakdown
      .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0))
      .map((r) => {
        const rate = r.employee?.hourly_rate || 0;
        const regH = r.regular_hours || 0;
        const otH = r.overtime_hours || 0;
        return [
          r.employee?.name || '',
          r.employee?.employee_number || '',
          r.employee?.position || '',
          `$${rate.toFixed(2)}`,
          regH.toFixed(2),
          otH.toFixed(2),
          (r.total_hours || 0).toFixed(2),
          `$${(regH * rate).toFixed(2)}`,
          `$${(otH * rate * 1.5).toFixed(2)}`,
          `$${(r.total_pay || 0).toFixed(2)}`,
        ];
      });

    autoTable(doc, {
      startY: summaryY + 22,
      head: [['Employee', 'ID', 'Position', 'Rate', 'Reg Hrs', 'OT Hrs', 'Total Hrs', 'Reg Pay', 'OT Pay', 'Total Pay']],
      body: tableData,
      foot: [[
        { content: `TOTALS (${tableData.length} employees)`, colSpan: 4, styles: { halign: 'left', fontStyle: 'bold' } },
        payrollSummary.employeeBreakdown.reduce((s, r) => s + (r.regular_hours || 0), 0).toFixed(2),
        totalOT.toFixed(2),
        payrollSummary.totalHours.toFixed(2),
        '',
        '',
        `$${payrollSummary.totalCost.toFixed(2)}`,
      ]],
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 30, 46], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [245, 245, 250], textColor: [30, 30, 46], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 255] },
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' },
        6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
      },
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 6;
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 160);
    doc.text('Overtime calculated per Mon–Sun work week: first 40 hrs at 1.0×, hours over 40 at 1.5×.', 14, finalY);

    doc.save(`payroll-${payrollSummary.startDate || format(new Date(), 'yyyy-MM-dd')}-to-${payrollSummary.endDate || format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payroll</h1>
          <p className="text-gray-500 mt-1">
            View hours and labor costs for{' '}
            {selectedLocation === 'all'
              ? 'all locations'
              : (locations || []).find((l) => l.id === selectedLocation)?.name}
          </p>
        </div>
        <div className="flex gap-2">
          {canEditMultiplier && (
            <Button onClick={() => setShowMultiplierSettings(true)} variant="outline" className="gap-2">
              <Settings className="w-4 h-4" />
              Cost Multiplier ({costMultiplier.toFixed(2)}x)
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-payroll-btn">
                <Download className="w-4 h-4" />
                Export
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Cost Multiplier Settings Modal (Accounting Admin only) */}
      <Dialog open={showMultiplierSettings} onOpenChange={setShowMultiplierSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Payroll Cost Multiplier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              The cost multiplier is applied to actual payroll to calculate Total Payroll Cost, 
              which includes taxes, benefits, and other employer costs.
            </p>
            <div className="space-y-2">
              <Label>Multiplier Value</Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                value={tempMultiplier}
                onChange={(e) => setTempMultiplier(e.target.value)}
                placeholder="e.g., 1.25"
              />
              <p className="text-xs text-gray-500">
                Example: 1.25 means Total Cost = Actual Payroll × 1.25 (25% overhead)
              </p>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
              <p><strong>Current setting:</strong> {costMultiplier.toFixed(2)}x</p>
              <p className="mt-1">Changes will apply to all payroll calculations.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMultiplierSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveMultiplier} className="bg-purple-600 hover:bg-purple-700">
              Save Multiplier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Level Indicator */}
      <Card className={`mb-6 border-0 shadow-md ${canViewSalaries ? 'bg-purple-50' : 'bg-gray-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${canViewSalaries ? 'bg-purple-100' : 'bg-gray-200'}`}>
                {canViewSalaries ? <Shield className="w-5 h-5 text-purple-600" /> : <Lock className="w-5 h-5 text-gray-500" />}
              </div>
              <div>
                <p className={`font-medium ${canViewSalaries ? 'text-emerald-800' : 'text-gray-700'}`}>
                  {canViewSalaries ? 'Accounting Admin Access' : 'Standard Access'}
                </p>
                <p className={`text-sm ${canViewSalaries ? 'text-purple-600' : 'text-gray-500'}`}>
                  {canViewSalaries 
                    ? 'You can view manager and assistant manager salaries' 
                    : 'Manager salaries are restricted to Accounting Admin only'}
                </p>
              </div>
            </div>
            {canViewSalaries && (
              <Button
                variant={showManagerSalaries ? 'default' : 'outline'}
                onClick={() => setShowManagerSalaries(!showManagerSalaries)}
                className={showManagerSalaries ? 'bg-purple-600 hover:bg-purple-700' : ''}
                data-testid="toggle-manager-salaries"
              >
                {showManagerSalaries ? <><Eye className="w-4 h-4 mr-2" />Showing Salaries</> : <><EyeOff className="w-4 h-4 mr-2" />Show Manager Salaries</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Period Tabs with Date Range Selector */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <Tabs value={period} onValueChange={(val) => {
              setPeriod(val);
              setUseCustomRange(val === 'custom');
            }}>
              <TabsList className="grid grid-cols-5 w-full max-w-lg">
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="annual">Annual</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>
            </Tabs>
            
            {/* Pay Period Presets */}
            {period !== 'custom' && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const first = new Date(now.getFullYear(), now.getMonth(), 1);
                    const fifteenth = new Date(now.getFullYear(), now.getMonth(), 15);
                    setCustomStartDate(first);
                    setCustomEndDate(fifteenth);
                    setPeriod('custom');
                    setUseCustomRange(true);
                  }}
                >
                  1st - 15th
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const sixteenth = new Date(now.getFullYear(), now.getMonth(), 16);
                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    setCustomStartDate(sixteenth);
                    setCustomEndDate(lastDay);
                    setPeriod('custom');
                    setUseCustomRange(true);
                  }}
                >
                  16th - End
                </Button>
              </div>
            )}
          </div>
          
          {/* Custom Date Range Picker */}
          {period === 'custom' && (
            <div className="flex items-center gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">From:</Label>
                <Input
                  type="date"
                  value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomStartDate(e.target.value ? parseISO(e.target.value) : null)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">To:</Label>
                <Input
                  type="date"
                  value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomEndDate(e.target.value ? parseISO(e.target.value) : null)}
                  className="w-40"
                />
              </div>
              <Button
                size="sm"
                onClick={loadPayrollData}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!customStartDate || !customEndDate}
              >
                Apply
              </Button>
            </div>
          )}
          
          {payrollSummary.startDate && payrollSummary.endDate && (
            <p className="text-sm text-gray-500 mt-3">
              Showing data from {format(parseISO(payrollSummary.startDate), 'MMM d, yyyy')} to{' '}
              {format(parseISO(payrollSummary.endDate), 'MMM d, yyyy')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-sm text-gray-400 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Manager Salaries Section */}
      {canViewSalaries && showManagerSalaries && payrollSummary.managerPayroll.length > 0 && (
        <Card className="mb-6 border-0 shadow-md border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Manager & Supervisor Salaries
              <Badge className="bg-red-100 text-red-700 ml-2">Confidential</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-red-50">
                  <TableHead>Manager</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Annual Salary</TableHead>
                  <TableHead className="text-right">{periodLabels[period]} Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollSummary.managerPayroll.map((record) => {
                  const location = (locations || []).find(l => l.id === record.manager?.managed_location);
                  return (
                    <TableRow key={record.manager?.id} className="hover:bg-red-50/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={record.manager?.avatar} />
                            <AvatarFallback className="bg-red-100 text-red-700">
                              {record.manager?.name?.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">{record.manager?.name}</p>
                            <p className="text-sm text-gray-500">{record.manager?.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{location?.name || 'All Locations'}</TableCell>
                      <TableCell className="text-right font-medium">${record.salary?.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold text-red-600">${record.period_salary?.toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="bg-red-50 p-4 border-t border-red-100">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">Total Manager Cost ({periodLabels[period]})</span>
                <span className="font-bold text-red-600 text-lg">${payrollSummary.totalManagerCost.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee Payroll Table */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Hourly Employee Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Employee ID</TableHead>
                {canViewSalaries && <TableHead className="text-right">Hourly Rate</TableHead>}
                <TableHead className="text-right">Regular Hours</TableHead>
                <TableHead className="text-right">Overtime Hours</TableHead>
                <TableHead className="text-right">Total Hours</TableHead>
                {canViewSalaries && <TableHead className="text-right">Total Pay</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {payrollSummary.employeeBreakdown
                .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0))
                .map((record) => {
                  const isManagerEmployee = record.employee?.is_manager_employee;
                  const hidePayInfo = isManagerEmployee && !canViewSalaries;
                  
                  return (
                    <TableRow key={record.employee?.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={record.employee?.avatar} />
                            <AvatarFallback className="bg-gray-200">
                              {record.employee?.name?.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">{record.employee?.name}</p>
                            <p className="text-sm text-gray-500">
                              {record.employee?.position}
                              {isManagerEmployee && <Badge variant="outline" className="ml-2 text-xs">Manager</Badge>}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {record.employee?.employee_number}
                        </span>
                      </TableCell>
                      {canViewSalaries && (
                        <TableCell className="text-right text-gray-600">
                          {hidePayInfo ? <span className="text-gray-400">—</span> : `$${record.employee?.hourly_rate?.toFixed(2)}`}
                        </TableCell>
                      )}
                      <TableCell className="text-right">{(record.regular_hours || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        {(record.overtime_hours || 0) > 0 ? (
                          <span className="text-amber-600 font-medium">{(record.overtime_hours || 0).toFixed(2)}</span>
                        ) : (
                          <span className="text-gray-400">0.00</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">{(record.total_hours || 0).toFixed(2)}</TableCell>
                      {canViewSalaries && (
                        <TableCell className="text-right font-semibold text-purple-600">
                          {hidePayInfo ? <span className="text-gray-400">—</span> : `$${(record.total_pay || 0).toLocaleString()}`}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {payrollSummary.employeeBreakdown.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No payroll data for this period</p>
            </div>
          )}

          {payrollSummary.employeeBreakdown.length > 0 && (
            <div className="bg-gray-50 p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">
                  Employee Total ({payrollSummary.employeeBreakdown.length} employees)
                </span>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Hours</p>
                    <p className="font-semibold text-gray-900">{payrollSummary.totalHours.toFixed(2)}</p>
                  </div>
                  {canViewSalaries && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Employee Cost</p>
                      <p className="font-bold text-purple-600 text-lg">${payrollSummary.totalCost.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grand Total */}
      {canViewSalaries && showManagerSalaries && (
        <Card className="mt-6 border-0 shadow-md bg-gradient-to-r from-slate-800 to-slate-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <p className="text-gray-300 text-sm">Total Labor Cost ({periodLabels[period]})</p>
                <p className="text-xs text-gray-400 mt-1">Employees + Managers</p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold">
                  ${(payrollSummary.totalCost + payrollSummary.totalManagerCost).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Payroll;
